import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff, Sparkles, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useAuth, apiClient } from "../App";
import { supabase } from "../lib/supabase";

const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const processedGoogleSessionRef = useRef(null);

  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
  });

  const finishGoogleLogin = useCallback(
    async (session) => {
      const supabaseUser = session?.user;
      if (!supabaseUser?.email) {
        return;
      }

      // Avoid duplicate sync calls for the same OAuth session.
      if (processedGoogleSessionRef.current === session.access_token) {
        return;
      }
      processedGoogleSessionRef.current = session.access_token;

      setLoading(true);

      try {
        const response = await apiClient.post("/auth/google", {
          email: supabaseUser.email,
          name:
            supabaseUser.user_metadata?.full_name ||
            supabaseUser.user_metadata?.name ||
            supabaseUser.email.split("@")[0],
          avatar_url:
            supabaseUser.user_metadata?.avatar_url ||
            supabaseUser.user_metadata?.picture ||
            null,
          supabase_id: supabaseUser.id,
          access_token: session.access_token,
        });

        if (response.data.token) {
          localStorage.setItem("auth_token", response.data.token);
          localStorage.setItem("auth_token_source", "backend");
        }

        setUser(response.data.user);
        const from = location.state?.from?.pathname || "/dashboard/opportunities";
        navigate(from, { replace: true });
      } catch (error) {
        // Keep user logged in with backend profile if available, even if Google sync fails.
        console.error("Google session sync failed (fallback to Supabase user):", error);
        try {
          const me = await apiClient.get("/auth/me");
          setUser(me.data);
          const from = location.state?.from?.pathname || "/dashboard/opportunities";
          navigate(from, { replace: true });
          toast.error("Google connected. We restored your account from server profile.");
          return;
        } catch (_meError) {
          // Ignore and continue with session-based fallback below.
        }

        setUser({
          id: supabaseUser.id,
          email: supabaseUser.email,
          name:
            supabaseUser.user_metadata?.full_name ||
            supabaseUser.user_metadata?.name ||
            supabaseUser.email.split("@")[0],
          avatar_url:
            supabaseUser.user_metadata?.avatar_url ||
            supabaseUser.user_metadata?.picture ||
            null,
          picture:
            supabaseUser.user_metadata?.picture ||
            supabaseUser.user_metadata?.avatar_url ||
            null,
          user_metadata: supabaseUser.user_metadata || {},
          identities: supabaseUser.identities || [],
          auth_provider: supabaseUser.app_metadata?.provider || "google",
        });

        const from = location.state?.from?.pathname || "/dashboard/opportunities";
        navigate(from, { replace: true });
        toast.error("Connected with Google, but profile sync failed. Retrying in background.");
      } finally {
        setLoading(false);
      }
    },
    [location.state?.from?.pathname, navigate, setUser]
  );

  useEffect(() => {
    let isMounted = true;

    const syncExistingSession = async () => {
      const { data, error } = await supabase.auth.getSession();

      if (!isMounted || error || !data?.session) {
        return;
      }

      await finishGoogleLogin(data.session);
    };

    syncExistingSession();

    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted || event !== "SIGNED_IN" || !session) {
        return;
      }

      finishGoogleLogin(session);
    });

    return () => {
      isMounted = false;
      listener?.subscription?.unsubscribe();
    };
  }, [finishGoogleLogin]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const response = await apiClient.post("/auth/login", {
          email: formData.email,
          password: formData.password,
        });
        if (response.data.token) {
          localStorage.setItem("auth_token", response.data.token);
          localStorage.setItem("auth_token_source", "backend");
        }
        setUser(response.data.user);
        toast.success("Welcome back!");

        const from = location.state?.from?.pathname || "/dashboard/opportunities";
        navigate(from, { replace: true });
      } else {
        const response = await apiClient.post("/auth/register", {
          email: formData.email,
          password: formData.password,
          name: formData.name,
        });
        if (response.data.token) {
          localStorage.setItem("auth_token", response.data.token);
          localStorage.setItem("auth_token_source", "backend");
        }
        setUser(response.data.user);
        toast.success("Account created successfully!");
        navigate("/onboarding", { replace: true });
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Something went wrong";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);

    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/auth`,
        queryParams: {
          prompt: "select_account",
        },
      },
    });

    if (error) {
      console.error(error);
      toast.error("Google sign in failed");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#111111] flex">
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-6 md:px-12 lg:px-24 py-12">
        <Button
          variant="ghost"
          className="absolute top-6 left-6 text-zinc-400 hover:text-white"
          onClick={() => navigate("/")}
          data-testid="back-to-home-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to home
        </Button>

        <div className="max-w-md mx-auto w-full">
          <div className="flex items-center gap-2 mb-12">
            <div className="w-10 h-10 rounded-xl bg-[#7C3AED] flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-black" />
            </div>
            <span
              className="font-bold text-2xl tracking-tight"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              SaaS-Radar
            </span>
          </div>

          <div className="mb-8">
            <h1
              className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-2"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              {isLogin ? "Welcome back" : "Create account"}
            </h1>
            <p className="text-zinc-400">
              {isLogin
                ? "Enter your credentials to access your account"
                : "Start discovering SaaS opportunities today"}
            </p>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full bg-white/5 border-[#1F1F1F] text-white hover:bg-white/10 rounded-xl py-6 mb-6"
            onClick={handleGoogleLogin}
            disabled={loading}
            data-testid="google-auth-btn"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-white/[0.08]"></div>
            <span className="text-xs text-zinc-500 uppercase tracking-wider">or</span>
            <div className="flex-1 h-px bg-white/[0.08]"></div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <div>
                <Label htmlFor="name" className="text-zinc-300 mb-2 block">Name</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Your name"
                  value={formData.name}
                  onChange={handleChange}
                  className="bg-[#111111] border-[#1F1F1F] rounded-xl px-4 py-6 text-white placeholder:text-zinc-600 focus:border-[#7C3AED] focus:ring-[#7C3AED]/50"
                  required={!isLogin}
                  data-testid="name-input"
                />
              </div>
            )}

            <div>
              <Label htmlFor="email" className="text-zinc-300 mb-2 block">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleChange}
                className="bg-[#111111] border-[#1F1F1F] rounded-xl px-4 py-6 text-white placeholder:text-zinc-600 focus:border-[#7C3AED] focus:ring-[#7C3AED]/50"
                required
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-zinc-300 mb-2 block">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  className="bg-[#111111] border-[#1F1F1F] rounded-xl px-4 py-6 text-white placeholder:text-zinc-600 focus:border-[#7C3AED] focus:ring-[#7C3AED]/50 pr-12"
                  required
                  minLength={6}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-semibold rounded-xl py-6 mt-2"
              disabled={loading}
              data-testid="auth-submit-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
              ) : (
                isLogin ? "Sign In" : "Create Account"
              )}
            </Button>
          </form>

          <p className="text-center mt-6 text-zinc-400">
            {isLogin ? "Don't have an account? " : "Already have an account? "}
            <button
              type="button"
              className="text-[#7C3AED] hover:text-[#6D28D9] font-medium transition-colors"
              onClick={() => setIsLogin(!isLogin)}
              data-testid="toggle-auth-mode-btn"
            >
              {isLogin ? "Sign up" : "Sign in"}
            </button>
          </p>
        </div>
      </div>

      <div className="hidden lg:flex w-1/2 bg-[#111111] items-center justify-center p-12 border-l border-[#1F1F1F]">
        <div className="relative w-full max-w-lg">
          <div
            className="absolute inset-0 opacity-40"
            style={{
              background: "radial-gradient(circle at center, rgba(124, 58, 237, 0.3) 0%, transparent 70%)",
            }}
          />
          <img
            src="https://static.prod-images.emergentagent.com/jobs/69a70e06-6a70-4194-a220-df5ba0126809/images/1388e6c6c6dc8cd855d2478eccced445f211a1bd79f8b9d24eb8950eac3fc956.png"
            alt="Dashboard preview"
            className="w-full rounded-2xl relative z-10"
          />
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
